/**
 * CaseUserSelect
 *
 * Async-paginated dropdown for selecting users scoped to a specific case,
 * backed by the /list-case-users-rds endpoint.
 * Mirrors the FirmUserSelect pattern — wraps AsyncPaginate with the
 * loadCaseUserOptions loader and shared AsyncSelect styles.
 *
 * @param {Object}            props
 * @param {string}            props.caseId           - Case ID to scope the user list (required)
 * @param {Object|Array|null} props.value            - Selected value(s)
 * @param {Function}          props.onChange          - Change handler
 * @param {boolean}           [props.teamMember]      - Filter to team members only (default false)
 * @param {string}            [props.placeholder]
 * @param {boolean}           [props.isClearable]
 * @param {boolean}           [props.isDisabled]
 * @param {boolean}           [props.isSearchable]
 * @param {boolean}           [props.isMulti]
 * @param {string}            [props.className]
 * @param {number}            [props.pageSize]
 * @param {Object}            [props.styles]
 * @param {string}            [props.name]
 * @param {string}            [props.id]
 * @param {Function}          [props.onBlur]
 * @param {Function}          [props.onFocus]
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { AsyncPaginate } from 'react-select-async-paginate';
import { loadCaseUserOptions } from 'hooks/queries/useCaseUsersDropdown';
import '../shared/AsyncSelect.scss';
import './CaseUserSelect.scss';

function CheckboxOption({ innerRef, innerProps, isSelected, isFocused, children }) {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        'async-select-custom__option',
        isFocused ? 'async-select-custom__option--is-focused' : '',
        isSelected ? 'async-select-custom__option--is-selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        ...innerProps.style,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'nowrap',
        overflow: 'hidden',
      }}
    >
      <span
        className={`case-user-checkbox${isSelected ? ' case-user-checkbox--checked' : ''}`}
        style={{ flexShrink: 0 }}
        aria-hidden="true"
      />
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span>
    </div>
  );
}

function CaseUserSelect({
  caseId = '',
  value = null,
  onChange,
  teamMember = false,
  placeholder = 'Select user',
  isClearable = true,
  isDisabled = false,
  isSearchable = true,
  isMulti = false,
  className = '',
  pageSize = 50,
  styles = {},
  name,
  id,
  onBlur,
  onFocus,
  ...rest
}) {
  const loadOptions = useMemo(
    () => (search, loadedOptions, additional) => loadCaseUserOptions(search, loadedOptions, additional, { pageSize, caseId, teamMember }),
    [pageSize, caseId, teamMember]
  );

  const mergedStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        minHeight: '38px',
        maxHeight: '38px',
        flexWrap: 'nowrap',
        overflow: 'hidden',
      }),
      valueContainer: (base) => ({
        ...base,
        flexWrap: 'nowrap',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }),
      multiValue: (base) => ({
        ...base,
        flexShrink: 0,
        minWidth: 'fit-content',
      }),
      menu: (base) => ({ ...base, zIndex: 9999, width: '100%', minWidth: 'unset' }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      option: (base) => ({
        ...base,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'nowrap',
        padding: '4px 8px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }),
      ...styles,
    }),
    [styles]
  );

  const components = useMemo(() => ({ Option: CheckboxOption }), []);

  return (
    <AsyncPaginate
      value={value}
      loadOptions={loadOptions}
      onChange={onChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      isMulti={isMulti}
      hideSelectedOptions={false}
      closeMenuOnSelect={!isMulti}
      className={`case-user-select-dropdown ${className}`.trim()}
      classNamePrefix="async-select-custom"
      styles={mergedStyles}
      components={components}
      additional={{ page: 1 }}
      debounceTimeout={300}
      name={name}
      id={id}
      onBlur={onBlur}
      onFocus={onFocus}
      loadingMessage={() => 'Loading users...'}
      noOptionsMessage={({ inputValue }) => (inputValue ? `No users found for "${inputValue}"` : 'No users available')}
      menuShouldScrollIntoView={false}
      closeMenuOnScroll={(e) => e.target === document}
      {...rest}
    />
  );
}

CaseUserSelect.propTypes = {
  caseId: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  teamMember: PropTypes.bool,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isMulti: PropTypes.bool,
  className: PropTypes.string,
  pageSize: PropTypes.number,
  styles: PropTypes.object,
  name: PropTypes.string,
  id: PropTypes.string,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
};

export default CaseUserSelect;
